import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LifeBuoy, Search, ArrowLeft, ChevronRight, Cog, BookOpen } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
    DISPO_GUIDES, DISPO_GUIDE_KEYS,
    SYSTEM_AUTOMATIONS, SYSTEM_AUTOMATION_KEYS,
} from "../lib/dispoGuides";

/**
 * Jun-2026 (User-Wunsch) — „Was kann TwoWheels?" Hilfe-/Onboarding-Übersicht.
 *
 * Für Admins UND eingeloggte Händler (inkl. Mitarbeiter/Verkäufer) zugänglich.
 * Zeigt zwei durchsuchbare Blöcke, beide zentral aus `dispoGuides.js` gepflegt:
 *   1) Was das System AUTOMATISCH im Hintergrund macht (E-Mails an wen, Aufträge,
 *      Nummernkreise, bereichsübergreifende Datenflüsse).
 *   2) Funktionen der Kaufabwicklung Schritt für Schritt.
 *
 * Routen: /hilfe  und  /admin/hilfe-uebersicht  (gleiche Seite).
 */
function matchFeatures(features, q) {
    if (!q) return features;
    return features.filter((f) => f.name.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q));
}

function buildSections(keys, map, q) {
    return keys.map((key) => {
        const g = map[key];
        const features = matchFeatures(g.features || [], q);
        const titleMatch = !q || g.title.toLowerCase().includes(q) || (g.step || "").toLowerCase().includes(q);
        return { key, ...g, features, visible: titleMatch || features.length > 0 };
    }).filter((s) => s.visible);
}

function SectionList({ sections, idPrefix }) {
    return (
        <div className="space-y-6">
            {sections.map((s) => (
                <section key={s.key} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`${idPrefix}-section-${s.key}`}>
                    <div className="flex items-center gap-3 px-5 py-3.5 bg-[#111827]">
                        <ChevronRight className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="min-w-0">
                            <h2 className="font-display font-black text-base text-white truncate">{s.title}</h2>
                            {s.step && <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{s.step}</div>}
                        </div>
                        <span className="ml-auto text-[11px] font-bold text-[#111827] bg-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num">{s.features.length}</span>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {s.features.map((f, i) => (
                            <li key={i} className="flex gap-3 px-5 py-3" data-testid={`${idPrefix}-feature-${s.key}-${i}`}>
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFCC00] shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{f.name}</div>
                                    <div className="text-[13px] text-gray-600 leading-snug">{f.desc}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}

export default function HelpOverviewPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");

    const allowed = user && (
        user.role === "admin" || user.role === "b2b" ||
        user.role === "zweiradverkaeufer" || Boolean(user.employer_dealer_id)
    );

    useEffect(() => {
        if (loading) return;
        if (!user || user === false || !allowed) navigate("/");
    }, [user, loading, allowed, navigate]);

    const q = query.trim().toLowerCase();
    const autoSections = useMemo(() => buildSections(SYSTEM_AUTOMATION_KEYS, SYSTEM_AUTOMATIONS, q), [q]);
    const featureSections = useMemo(() => buildSections(DISPO_GUIDE_KEYS, DISPO_GUIDES, q), [q]);

    const totalFeatures = useMemo(() => {
        const a = SYSTEM_AUTOMATION_KEYS.reduce((n, k) => n + (SYSTEM_AUTOMATIONS[k].features?.length || 0), 0);
        const b = DISPO_GUIDE_KEYS.reduce((n, k) => n + (DISPO_GUIDES[k].features?.length || 0), 0);
        return a + b;
    }, []);

    if (loading || !user || !allowed) return <div className="p-12">Lädt…</div>;

    const isAdmin = user.role === "admin";
    const noResults = autoSections.length === 0 && featureSections.length === 0;

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="help-overview-page">
            <Link to={isAdmin ? "/admin" : "/dashboard"} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#111827] mb-4" data-testid="help-back">
                <ArrowLeft className="w-4 h-4" /> {isAdmin ? "Zurück zur Admin-Übersicht" : "Dashboard"}
            </Link>

            <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Hilfe & Onboarding</div>
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
                        <LifeBuoy className="w-8 h-8 text-[#FFCC00]" /> Was kann TwoWheels?
                    </h1>
                </div>
                <div className="text-xs text-gray-500">{totalFeatures} Funktionen · automatisch &amp; manuell</div>
            </div>
            <p className="text-gray-600 mb-6">
                Alles, was TwoWheels für dich erledigt — inklusive der Hintergrund-Automatik: welche E-Mails an wen
                versendet werden, welche Aufträge automatisch entstehen und wie Daten zwischen Disposition, Werkstatt,
                Warenwirtschaft und Buchhaltung fließen. Zentral gepflegt, durchsuchbar.
            </p>

            <div className="relative mb-8">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Funktion, E-Mail-Auslöser, Bereich oder Stichwort suchen…"
                    className="w-full h-12 pl-11 pr-4 rounded-sm border-2 border-gray-200 focus:border-[#FFCC00] focus:outline-none text-sm"
                    data-testid="help-search"
                />
            </div>

            {noResults ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500" data-testid="help-empty">
                    Keine Treffer für „{query}".
                </div>
            ) : (
                <div className="space-y-10">
                    {autoSections.length > 0 && (
                        <div data-testid="help-automations-block">
                            <h2 className="font-display text-xl font-black mb-3 flex items-center gap-2">
                                <Cog className="w-6 h-6 text-[#FFCC00]" /> Das macht TwoWheels automatisch
                            </h2>
                            <SectionList sections={autoSections} idPrefix="help-auto" />
                        </div>
                    )}
                    {featureSections.length > 0 && (
                        <div data-testid="help-features-block">
                            <h2 className="font-display text-xl font-black mb-3 flex items-center gap-2">
                                <BookOpen className="w-6 h-6 text-[#FFCC00]" /> Funktionen der Kaufabwicklung
                            </h2>
                            <SectionList sections={featureSections} idPrefix="help-feat" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
